import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, ChevronDown, UserRoundCheck, UserRoundX } from 'lucide-react';

import {
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  UserStatusBadge,
} from '@klareospace/pharos-lib-web-utils';
import type { User } from '@klareospace/pharos-lib-types';
import { USER_STATUS } from '@klareospace/pharos-lib-types';

import { useCancelInvitation } from '../hooks/use-cancel-invitation';
import { useUpdateUserStatus } from '../hooks/use-update-user-status';

// ─── Types ────────────────────────────────────────────────────────────────────

type ConfirmAction = 'activate' | 'cancelInvitation' | 'deactivate' | null;

export interface UserStatusHeaderActionProps {
  onStatusChange?: () => void;
  user: User;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function UserStatusHeaderAction({ onStatusChange, user }: UserStatusHeaderActionProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');
  const cancelInvitation = useCancelInvitation();
  const updateUserStatus = useUpdateUserStatus();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

  const isPending = cancelInvitation.isPending || updateUserStatus.isPending;
  const userName = `${user.firstName} ${user.lastName}`.trim();

  function handleConfirm(): void {
    if (isPending) return;

    switch (confirmAction) {
      case 'cancelInvitation':
        cancelInvitation.mutate(user.id, { onSuccess: onStatusChange });
        break;
      case 'deactivate':
        updateUserStatus.mutate({ id: user.id, status: USER_STATUS.INACTIVE }, { onSuccess: onStatusChange });
        break;
      case 'activate':
        updateUserStatus.mutate({ id: user.id, status: USER_STATUS.ACTIVE }, { onSuccess: onStatusChange });
        break;
    }

    setConfirmAction(null);
  }

  function getDialogLabels(): { cancel: string; confirm: string; description: string; title: string } {
    switch (confirmAction) {
      case 'cancelInvitation':
        return {
          cancel: t('users.actions.cancelInvitation.dialog.cancel'),
          confirm: t('common.cancelInvitation'),
          description: t('users.actions.cancelInvitation.dialog.description', { name: userName }),
          title: t('common.cancelInvitation'),
        };
      case 'deactivate':
        return {
          cancel: tCommon('actions.cancel'),
          confirm: tCommon('actions.deactivate'),
          description: t('users.actions.deactivate.dialog.description', { name: userName }),
          title: t('users.actions.deactivate.dialog.title'),
        };
      case 'activate':
        return {
          cancel: tCommon('actions.cancel'),
          confirm: tCommon('actions.reactivate'),
          description: t('users.actions.reactivate.dialog.description', { name: userName }),
          title: t('users.actions.reactivate.dialog.title'),
        };
      default:
        return { cancel: '', confirm: '', description: '', title: '' };
    }
  }

  const dialogLabels = getDialogLabels();

  const statusToDisplay = user.status;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex cursor-pointer items-center gap-1">
            <UserStatusBadge label={tCommon(`statuses.${statusToDisplay}`)} solid status={statusToDisplay} />
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {statusToDisplay === USER_STATUS.PENDING && (
            <DropdownMenuItem disabled={isPending} onClick={(): void => setConfirmAction('cancelInvitation')}>
              <Ban className="me-2 h-4 w-4" />
              {t('common.cancelInvitation')}
            </DropdownMenuItem>
          )}
          {statusToDisplay === USER_STATUS.ACTIVE && (
            <DropdownMenuItem disabled={isPending} onClick={(): void => setConfirmAction('deactivate')}>
              <UserRoundX className="me-2 h-4 w-4" />
              {t('users.edit.actions.deactivate')}
            </DropdownMenuItem>
          )}
          {statusToDisplay === USER_STATUS.INACTIVE && (
            <DropdownMenuItem disabled={isPending} onClick={(): void => setConfirmAction('activate')}>
              <UserRoundCheck className="me-2 h-4 w-4" />
              {t('users.edit.actions.activate')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        cancelLabel={dialogLabels.cancel}
        confirmLabel={dialogLabels.confirm}
        description={dialogLabels.description}
        isPending={isPending}
        onConfirm={handleConfirm}
        onOpenChange={(open): void => {
          if (!open) setConfirmAction(null);
        }}
        open={!!confirmAction}
        title={dialogLabels.title}
      />
    </>
  );
}
